import { useEffect, useState } from "react";
import { Plus, Pencil, Power } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const StatusBadge = (r) => <Badge className={r.is_active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-200 text-slate-600"}>{r.is_active ? "Active" : "Inactive"}</Badge>;

function SimpleMaster({ endpoint, codeKey, codeLabel, nameLabel, managePerm, testid, label }) {
  const { has } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "" });
  const [refresh, setRefresh] = useState(0);
  const canManage = has(managePerm);

  const openCreate = () => { setEditing(null); setForm({ code: "", name: "" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ code: r[codeKey], name: r.type_name || r.name }); setOpen(true); };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error("Code and name are required"); return; }
    try {
      const nameField = endpoint.includes("types") ? "type_name" : "name";
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, { [nameField]: form.name });
      } else {
        await api.post(endpoint, { [codeKey]: form.code, [nameField]: form.name });
      }
      toast.success(`${label} saved`);
      setOpen(false); setRefresh((n) => n + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const toggle = async (r) => { try { await api.patch(`${endpoint}/${r.id}/status?is_active=${!r.is_active}`); setRefresh((n) => n + 1); } catch { toast.error("Failed"); } };

  const columns = [
    { key: codeKey, label: codeLabel, mono: true },
    { key: "name", label: nameLabel, render: (r) => <span className="font-medium text-slate-900">{r.type_name || r.name}</span> },
    { key: "status", label: "Status", render: StatusBadge },
  ];

  return (
    <div>
      {canManage && <div className="mb-3 flex justify-end"><Button data-testid={`${testid}-add`} onClick={openCreate} size="sm" className="bg-blue-600 hover:bg-blue-700"><Plus className="mr-1.5 h-4 w-4" /> New {label}</Button></div>}
      <DataTable
        testid={`${testid}-table`}
        columns={columns}
        searchPlaceholder={`Search ${label.toLowerCase()}...`}
        refreshKey={refresh}
        load={(params) => api.get(endpoint, { params }).then((r) => r.data)}
        rowActions={canManage ? (r) => (
          <div className="flex justify-end gap-1">
            <Button data-testid={`${testid}-edit-${r.id}`} variant="ghost" size="sm" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
            <Button data-testid={`${testid}-toggle-${r.id}`} variant="ghost" size="sm" onClick={() => toggle(r)}><Power className={`h-4 w-4 ${r.is_active ? "text-red-500" : "text-emerald-500"}`} /></Button>
          </div>
        ) : null}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{editing ? `Edit ${label}` : `New ${label}`}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{codeLabel} *</Label><Input data-testid={`${testid}-code-input`} disabled={!!editing} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1" /></div>
            <div><Label>{nameLabel} *</Label><Input data-testid={`${testid}-name-input`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid={`${testid}-save`} onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubTypeMaster() {
  const { has } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ subtype_code: "", name: "", material_type_id: "" });
  const [refresh, setRefresh] = useState(0);
  const [types, setTypes] = useState([]);
  const canManage = has("MATERIAL_SUBTYPE.MANAGE");

  useEffect(() => { api.get("/material-types", { params: { page_size: 500 } }).then((r) => setTypes(r.data.items || [])); }, []);

  const openCreate = () => { setEditing(null); setForm({ subtype_code: "", name: "", material_type_id: "" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ subtype_code: r.subtype_code, name: r.name, material_type_id: r.material_type_id }); setOpen(true); };

  const save = async () => {
    if (!form.subtype_code.trim() || !form.name.trim() || !form.material_type_id) { toast.error("Code, name and primary type are required"); return; }
    try {
      if (editing) await api.put(`/material-subtypes/${editing.id}`, { name: form.name, material_type_id: form.material_type_id });
      else await api.post("/material-subtypes", form);
      toast.success("Sub-type saved"); setOpen(false); setRefresh((n) => n + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const toggle = async (r) => { try { await api.patch(`/material-subtypes/${r.id}/status?is_active=${!r.is_active}`); setRefresh((n) => n + 1); } catch { toast.error("Failed"); } };

  const columns = [
    { key: "subtype_code", label: "Code", mono: true },
    { key: "name", label: "Sub-Type", render: (r) => <span className="font-medium text-slate-900">{r.name}</span> },
    { key: "type_name", label: "Primary Type", render: (r) => <Badge variant="secondary">{r.type_name}</Badge> },
    { key: "status", label: "Status", render: StatusBadge },
  ];

  return (
    <div>
      {canManage && <div className="mb-3 flex justify-end"><Button data-testid="subtype-add" onClick={openCreate} size="sm" className="bg-blue-600 hover:bg-blue-700"><Plus className="mr-1.5 h-4 w-4" /> New Sub-Type</Button></div>}
      <DataTable
        testid="subtypes-table"
        columns={columns}
        searchPlaceholder="Search sub-types..."
        refreshKey={refresh}
        filters={[{ key: "material_type_id", label: "Type", options: types.map((t) => ({ value: t.id, label: t.type_name })) }]}
        load={(params) => api.get("/material-subtypes", { params }).then((r) => r.data)}
        rowActions={canManage ? (r) => (
          <div className="flex justify-end gap-1">
            <Button data-testid={`subtype-edit-${r.id}`} variant="ghost" size="sm" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => toggle(r)}><Power className={`h-4 w-4 ${r.is_active ? "text-red-500" : "text-emerald-500"}`} /></Button>
          </div>
        ) : null}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Sub-Type" : "New Sub-Type"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Sub-Type Code *</Label><Input data-testid="subtype-code-input" disabled={!!editing} value={form.subtype_code} onChange={(e) => setForm({ ...form, subtype_code: e.target.value })} className="mt-1" /></div>
            <div><Label>Sub-Type Name *</Label><Input data-testid="subtype-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>Primary Material Type *</Label>
              <Select value={form.material_type_id} onValueChange={(v) => setForm({ ...form, material_type_id: v })}>
                <SelectTrigger data-testid="subtype-type-select" className="mt-1"><SelectValue placeholder="Select primary type" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.type_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="subtype-save" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function MaterialMasters() {
  return (
    <div>
      <PageHeader title="Material Masters" subtitle="Governed Material Types, Sub-Types and Makes" />
      <Tabs defaultValue="types">
        <TabsList data-testid="material-masters-tabs">
          <TabsTrigger value="types" data-testid="tab-types">Material Types</TabsTrigger>
          <TabsTrigger value="subtypes" data-testid="tab-subtypes">Sub-Types</TabsTrigger>
          <TabsTrigger value="makes" data-testid="tab-makes">Makes</TabsTrigger>
        </TabsList>
        <TabsContent value="types" className="mt-4">
          <SimpleMaster endpoint="/material-types" codeKey="type_code" codeLabel="Type Code" nameLabel="Type Name" managePerm="MATERIAL_TYPE.MANAGE" testid="type" label="Type" />
        </TabsContent>
        <TabsContent value="subtypes" className="mt-4"><SubTypeMaster /></TabsContent>
        <TabsContent value="makes" className="mt-4">
          <SimpleMaster endpoint="/material-makes" codeKey="make_code" codeLabel="Make Code" nameLabel="Make Name" managePerm="MATERIAL_MAKE.MANAGE" testid="make" label="Make" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
